import React from "react";
import { VariantProps } from "@stitches/react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { colors, CSS, styled, vars } from "@puzzle/theme";

type ColorKey = keyof typeof colors;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Fallback = AvatarPrimitive.Fallback;
const Root = styled(AvatarPrimitive.Root, {
  margin: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "$body",
  fontWeight: "$heavy",
  textTransform: "uppercase",
  borderRadius: "$ellipse",
  userSelect: "none",

  defaultVariants: {
    size: "medium",
  },

  variants: {
    size: {
      mini: {
        width: 20,
        height: 20,
        fontSize: 10,
      },

      small: {
        width: 28,
        height: 28,
        fontSize: "$bodyXS",
      },

      medium: {
        width: 32,
        height: 32,
      },
    },
  },
});

const EMPTY_CHAR = "?";
const getInitials = (name?: string, email?: string) => {
  if (name) {
    const result = name
      .split(" ")
      .slice(0, 2)
      .map((x) => x.charAt(0))
      .join("");

    if (result.length < 2) {
      return name.substr(0, 2);
    }

    return result;
  } else if (email) {
    return email.substr(0, 2);
  }

  return EMPTY_CHAR;
};

type AvatarProps = VariantProps<typeof Root> & {
  user: { email?: string | null; name?: string | null };
  color?: ColorKey;
  textColor?: ColorKey;
  css?: CSS;
};

export const Avatar = ({ user, color, textColor, css, ...props }: AvatarProps) => {
  const { name, email } = user;
  const initials = getInitials(name ?? "", email ?? "");
  const backgroundColor = color ? vars.colors[color] : vars.colors["surface-transparent-base"];
  const fontColor = textColor ?? vars.colors["text-neutral-base"];
  const fontSize = initials === EMPTY_CHAR ? "$bodyL" : undefined;

  return (
    <Root
      {...props}
      css={{
        color: fontColor,
        backgroundColor,
        fontSize,
        ...css,
      }}
    >
      <Fallback>{initials}</Fallback>
    </Root>
  );
};
